import React from 'react';
import CompanyBubble from './CompanyBubble';

const FunnelStage = ({ 
  stage, 
  companies, 
  maxRevenue, 
  onDrop,
  index,
  totalStages,
  stageWidth,
  totalHeight
}) => {
  
  // Cubic Bezier helper to calculate Y position at time t (0 to 1)
  const cubicBezier = (t, p0, p1, p2, p3) => {
    const invT = 1 - t;
    return (invT ** 3) * p0 + 
           3 * (invT ** 2) * t * p1 + 
           3 * invT * (t ** 2) * p2 + 
           (t ** 3) * p3;
  };

  // Calculate the top and height for a specific sub-column
  // This must match the math in FunnelBackground.tsx
  const getBoundaries = (subIndex, totalSubs) => {
    // Defines
    const neckHeight = totalHeight * 0.30;
    const neckTop = (totalHeight - neckHeight) / 2;
    const neckBottom = neckTop + neckHeight;
    const mouthTop = totalHeight * 0.22;
    const mouthBottom = totalHeight * 0.78;

    // Calculate 't' (horizontal progress through this specific stage)
    // We want the center of the column for estimation
    const subColWidth = 1 / totalSubs;
    const tStart = subIndex * subColWidth;
    const tCenter = tStart + (subColWidth / 2);

    let topY, bottomY;

    if (index === 0) {
      // SALES STAGE: Curves from Mouth to Neck
      // CP1 = 0.5, CP2 = 0.5 relative to the stage width
      // Since t is 0..1 across the stage width, we can map directly to Bezier
      topY = cubicBezier(tCenter, mouthTop, mouthTop, neckTop, neckTop);
      bottomY = cubicBezier(tCenter, mouthBottom, mouthBottom, neckBottom, neckBottom);
    } else if (index === totalStages - 1) {
      // MARKETING STAGE: Curves from Neck to Mouth
      topY = cubicBezier(tCenter, neckTop, neckTop, mouthTop, mouthTop);
      bottomY = cubicBezier(tCenter, neckBottom, neckBottom, mouthBottom, mouthBottom);
    } else {
      // MIDDLE STAGES: Flat Neck
      topY = neckTop;
      bottomY = neckBottom;
    }

    // Add some padding so bubbles don't touch the lines
    const padding = 12;
    return {
      top: topY + padding,
      height: bottomY - topY - (padding * 2)
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, subLabel) => {
    e.preventDefault();
    const companyId = e.dataTransfer.getData('companyId');
    if (companyId) {
      onDrop(companyId, stage.id, subLabel);
    }
  };

  return (
    <div 
      className="stage-container"
      style={{ width: `${stageWidth}px`, height: `${totalHeight}px` }} 
    >
      {/* Header Container (Fixed Top) */}
      <div className="stage-header-wrapper">
        
        {/* Chevron Header */}
        <div 
           className="stage-chevron"
           style={{
             clipPath: 'polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0% 100%)',
             marginRight: index === totalStages - 1 ? 0 : '-10px',
             paddingRight: index === totalStages - 1 ? 0 : '15px'
           }}
        >
          <h3 className="stage-title">{stage.name}</h3>
        </div>

        {/* Sub-labels Row - These act as column headers */}
        <div className="sub-labels-row">
          {stage.subLabels.map((sub, i) => (
            <div 
              key={i} 
              className="sub-label-cell"
            >
              <span className="sub-label-text">
                {sub}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Column Grid */}
      <div className="stage-column-grid">
         {stage.subLabels.map((subLabel, subIndex) => {
            const bounds = getBoundaries(subIndex, stage.subLabels.length);
            
            // Filter companies for this specific sub-column
            const columnCompanies = companies.filter(c => c.subStageId === subLabel);

            return (
              <div 
                key={subLabel}
                className="stage-sub-column"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, subLabel)}
              >
                  {/* Bubble Container positioned absolutely within the column */}
                  <div 
                    className="bubble-list-container custom-scrollbar"
                    style={{
                       top: Math.max(0, bounds.top - 116),
                       height: bounds.height
                    }}
                  >
                     {columnCompanies.map(company => (
                        <CompanyBubble 
                          key={company.id} 
                          company={company} 
                          maxRevenue={maxRevenue} 
                        />
                      ))}
                      
                      {/* Drop Target Helper (visible only when empty or dragging over) */}
                      {columnCompanies.length === 0 && (
                        <div className="drop-target-helper">
                            <div className="drop-dot"></div>
                        </div>
                      )}
                  </div>
              </div>
            );
         })}
      </div>
    </div>
  );
};

export default FunnelStage;